<template>
  <BaseWidget
    :class="['app-preview-widget', widget.type]"
    :loading="loading" 
    :widget-options="widgetMenuOptions" 
    :content-link-to="contentLinkTo"
    :ext-link="(widget.type === 'invoices')"
    @refresh-data="refreshData" 
    @set-config="setConfig"
    v-on="$listeners"
  >
    <template v-slot:header>
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ subtitle }} <span class="text-lowercase">{{ periodName }}</span></div>
    </template>
    <template v-slot:content>
      <div v-if="amount > 0" class="chart">
        <div class="chart-content">
          <div class="donut">
            <DoughnutChart v-if="!loading" :data="chartData" :options="chartOptions" />
          </div>
          <div class="amount">
            <svg viewBox="-50 -50 100 100" preserveAspectRatio="xMidYMid meet" fill="currentColor">
              <text font-size="35" dy=".35em">{{ amount }}</text>
            </svg>
          </div>
        </div>
      </div>
      <div v-if="amount > 0" class="data">
        <!-- <div class="widget-title">{{ title }}</div> -->
        <div class="amount amount--main" :style="{'color': chartColors.primary }">{{ amountSymbol }}{{ totalPrimary | formatNumber }} <span>{{ primaryAmountConcept }}</span></div>
        <div class="amount amount--secondary" :style="{'color': chartColors.secondary }">{{ amountSymbol }}{{ totalSecondary | formatNumber }} <span>{{ secondaryAmountConcept }}</span></div>
        <div v-if="totalTertiary" class="amount amount--tertiary" :style="{'color': chartColors.tertiary }">{{ amountSymbol }}{{ totalTertiary | formatNumber }} <span>{{ tertiaryAmountConcept }}</span></div>
      </div>

      <div v-if="amount === 0 && widget.type === 'trips' && linkAddNew" class="missing">
        <font-awesome-icon icon="fa-solid fa-car" />
        <span>{{ $gettext('No entries in this period') }}. <br><router-link v-if="showCreate" :to="addNewLink" class="text-blue">{{ $gettext('Create a new trip') }}</router-link></span>
      </div>
      <div v-if="amount === 0 && widget.type === 'hours' && linkAddNew" class="missing">
        <font-awesome-icon icon="fa-regular fa-clock" />
        <span>{{ $gettext('No entries in this period') }}. <br><router-link v-if="showCreate" :to="addNewLink" class="text-blue">{{ $gettext('Create a new hour') }}</router-link></span>
      </div>
      <div v-if="amount === 0 && widget.type === 'costs' && linkAddNew" class="missing">
        <font-awesome-icon icon="fa-solid fa-cart-shopping" />
        <span>{{ $gettext('No entries in this period') }}. <br><router-link v-if="showCreate" :to="addNewLink" class="text-blue">{{ $gettext('Create a new cost') }}</router-link></span>
      </div>
      <div v-if="amount === 0 && widget.type === 'invoices' && linkAddNew" class="missing">
        <font-awesome-icon icon="fa-regular fa-file-lines" />
        <span>{{ $gettext('No entries in this period') }}. <br><a v-if="showCreate" :href="addNewLink" class="text-blue">{{ $gettext('Create a new invoice') }}</a></span>
      </div>
    </template>
    <!-- <template v-slot:actions>
      <div v-if="amount > 0">
        <router-link :to="Urls[linkTo]()">{{ $gettext('View all') }}</router-link>
      </div>
    </template> -->
  </BaseWidget>
</template>

<script>
  import { mapGetters } from 'vuex'
  import moment from 'moment'

  import DoughnutChart from '@/components/dashboard/charts/Doughnut'
  import BaseWidget from '@/components/dashboard/widgets/BaseWidget'

  import DashboardService from '@/store/services/dashboard.service.js'

  export default {
    name: 'app-preview-widget',
    components: { BaseWidget, DoughnutChart },
    props: {
      widget: {
        type: Object,
        default: () => {}
      },
    },
    data() {
      return {
        loading: true,
        title: '',
        subtitle: '',
        primaryAmountConcept: '',
        secondaryAmountConcept: '',
        amount: 0,
        totalPrimary: 0,
        totalSecondary: 0,
        totalTertiary: false,
        endpoint: '',
        linkAddNew: false,
        linkTo: '',
        showCreate: true,
        amountSymbol: '€',
        chartColors: {
          primary: '',
          secondary: '',
          tertiary: '#b9b4b47d' // $colorUnassigned
        },
        periodName: '',
        periodNames: {
          0: this.$gettext('Any period'),
          'TQ': this.$gettext('This quarter'),
          'PQ': this.$gettext('Previous quarter'),
          30: this.$gettext('1 month back'),
          90: this.$gettext('3 months back'),
          365: this.$gettext('12 months back')
        },
        config: { period: { days: 0 } },
        filters: {
          start_date: '',
          end_date: '',
          sender_company: '',
          owner_company: ''
        },
        chartOptions: {
          cutoutPercentage: 75,
          hoverBorderWidth: 0,
          legend: { display: false },
          tooltips: { enabled: false, displayColors: false }
        },
        chartData: {}
      }
    },
    computed: {
      widgetMenuOptions () {
        if (this.loading) return []
        var options = [
          { name: this.periodNames[0], event: { name: 'set-config', config: { period: { days: false } } } },
          { name: this.periodNames['TQ'], event: { name: 'set-config', config: { period: { days: 'TQ' } } } },
          { name: this.periodNames['PQ'], event: { name: 'set-config', config: { period: { days: 'PQ' } } } },
          { name: this.periodNames[30], event: { name: 'set-config', config: { period: { days: 30 } } } },
          { name: this.periodNames[90], event: { name: 'set-config', config: { period: { days: 90 } } } },
          { name: this.periodNames[365], event: { name: 'set-config', config: { period: { days: 365 } } } },
        ]
        if(this.currentRights[this.widget.type]){
          options.push({ separator: true });
          options.push({ name: 'Open ' + this.title, event: { name: 'link', link: this.contentLinkTo }, icon: 'icon-link' });
        }
        return options
      },
      contentLinkTo() {
        if (this.linkTo in window.Urls)
          return window.Urls[this.linkTo]()
        else
          return this.linkTo
      },
      addNewLink() {
        if (this.linkAddNew in window.Urls)
          return window.Urls[this.linkAddNew]()
        else
          return this.linkAddNew
      },
      ...mapGetters({ 
        currentCompany: "main/getCurrentCompany",
        currentRights: "main/getCurrentRights",
        getCompanyId: 'main/getCompanyId'
      })
    },
    mounted () {
      this.setupWidget()
      if(this.currentRights[this.widget.type]){
        this.selectPeriod(this.widget.config)
      }else{
        this.loading = false;
        this.showCreate = false;
      }
    },
    methods: {
      setConfig (config) {
        if (config.period) {
          this.selectPeriod(config)
        }
        this.$emit('save-config', this.config)
      },
      selectPeriod (config) {
        this.setupPeriod((config.period?.days) ? config.period.days : false)
        this.setPeriodFilters()
        this.refreshData()
      },
      refreshData () {
        if(this.currentRights[this.widget.type]) {
          this.getData()
        }
      },
      setupPeriod (days) {
        this.config.period.days = days
        this.periodName = (days) ? this.periodNames[days] : this.periodNames[0]
      },
      setPeriodFilters () {
        if (this.config.period.days) {
          if (this.config.period.days === 'TQ') {

            this.filters.start_date =  moment().startOf('quarter').format('YYYY-MM-DD')
            this.filters.end_date = moment().endOf('quarter').format('YYYY-MM-DD')

          } else  if(this.config.period.days === 'PQ') {
            
            this.filters.start_date =  moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD')
            this.filters.end_date = moment().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD')

          } else {
            const end_date = new Date()  
            const start_date = new Date(new Date().setDate(end_date.getDate() - this.config.period.days + ((this.config.period.days === 90) ? 0 : 1)))
            this.filters.end_date = moment(end_date).format('YYYY-MM-DD')
            this.filters.start_date = moment(start_date).format('YYYY-MM-DD')
          }

        } else {
          this.filters.end_date = ''
          this.filters.start_date = ''
        }
        this.filters.sender_company = this.getCompanyId
        this.filters.owner_company = this.getCompanyId
        this.filters.company_profile = this.getCompanyId
      },
      getData () {
        this.loading = true
        DashboardService[this.endpoint](this.filters).then((response) => {
          switch (this.widget.type) {
            case 'costs':
              this.amount = response.data.count
              this.totalPrimary = response.data.total_net
              this.totalSecondary = response.data.total_vat
              break;
            case 'hours':
              this.amount = response.data.total_count
              this.totalPrimary = response.data.invoiced
              this.totalSecondary = response.data.uninvoiced
              this.totalTertiary = response.data.unassigned
              break;
            case 'trips':
              this.amount = response.data.trip_count
              this.totalPrimary = response.data.total_business
              this.totalSecondary = response.data.total_private
              break;
            case 'invoices':
              this.amount = response.data.count
              this.totalPrimary = response.data.total_net
              this.totalSecondary = response.data.total_vat
              break;
          }
          this.chartData = {
            labels: [this.primaryAmountConceptShort, this.secondaryAmountConceptShort, this.tertiaryAmountConceptShort],
            datasets: [
              {
                backgroundColor: [this.chartColors.primary, this.chartColors.secondary, this.chartColors.tertiary],
                data: [this.totalPrimary, this.totalSecondary, this.totalTertiary],
                borderWidth: 0,
                hoverBorderWidth: 0
              }
            ]
          }
          this.loading = false
        })
      },
      setupWidget () {
        switch (this.widget.type) {
          case 'costs':
            this.endpoint = 'appPreviewCosts'
            this.title = this.$gettext('Costs overview')
            this.subtitle = this.$gettext('All costs from')
            this.primaryAmountConcept = this.$gettext('NET')
            this.secondaryAmountConcept = this.$gettext('VAT')
            this.primaryAmountConceptShort = this.$gettext('Net')
            this.secondaryAmountConceptShort = this.$gettext('Vat')
            this.chartColors.primary = '#22AC8A' // $colorBrand

            if (this.$route.path.includes('control-panel')) {
              this.linkTo = `${window.Urls['accountants:costs'](this.getCompanyId)}`
              this.linkAddNew = `${window.Urls['accountants:add_costs'](this.getCompanyId)}`
            } else {
              this.linkTo = 'costs_display_costs'
              this.linkAddNew = 'costs_add_cost'
            }

            break;
          case 'hours':
            this.endpoint = 'appPreviewHours'
            this.title = this.$gettext('Hours overview')
            this.subtitle = this.$gettext('All hours from')
            this.primaryAmountConcept = this.$gettext('INVOICED')
            this.secondaryAmountConcept = this.$gettext('UNINVOICED')
            this.tertiaryAmountConcept = this.$gettext('UNASSIGNED')
            this.primaryAmountConceptShort = this.$gettext('Inv')
            this.secondaryAmountConceptShort = this.$gettext('Un')
            this.tertiaryAmountConceptShort = this.$gettext('Unass')
            this.chartColors.primary = '#E9B641' // $colorAppHours
            this.chartColors.secondary = '#913b55' // $colorUninvoiced
            this.linkTo = 'hours_home'
            this.linkAddNew = 'hours_home' // FIXME
            this.amountSymbol = ''
            break;
          case 'trips':
            this.endpoint = 'appPreviewTrips'
            this.title = this.$gettext('Trips overview')
            this.subtitle = this.$gettext('All trips from')
            this.primaryAmountConcept = this.$gettext('Km WORK')
            this.secondaryAmountConcept = this.$gettext('Km PRIVATE')
            this.primaryAmountConceptShort = this.$gettext('Wrk')
            this.secondaryAmountConceptShort = this.$gettext('Prv')
            this.chartColors.primary = '#EC602C' // $colorAppTrips
            this.linkTo = 'trips_home'
            this.linkAddNew = 'trips_add_trip'
            this.amountSymbol = ''
            break;
          case 'invoices':
            this.endpoint = 'appPreviewInvoices'
            this.title = this.$gettext('Invoices overview')
            this.subtitle = this.$gettext('All invoices from')
            this.primaryAmountConcept = this.$gettext('NET')
            this.secondaryAmountConcept = this.$gettext('VAT')
            this.primaryAmountConceptShort = this.$gettext('Net')
            this.secondaryAmountConceptShort = this.$gettext('Vat')
            this.chartColors.primary = '#559FC9' // $colorAppInvoices
            this.linkTo = 'invoices_display_invoices'
            this.linkAddNew = 'invoices_add_invoice_step1'

            if (this.$route.path.includes('control-panel')) {
              this.linkTo = `${window.Urls['accountants:invoices'](this.getCompanyId)}`
              this.linkAddNew = `${window.Urls['accountants:invoices'](this.getCompanyId)}` // No 'add invoices' available for accountants
            } else {
              this.linkTo = 'invoices_display_invoices'
              this.linkAddNew = 'invoices_add_invoice_step1'
            }

            break;
        }
        this.chartColors.secondary = this.chartColors.primary + '7d'
      }
    }
  }
</script>

<style lang="scss" scoped>
.app-preview-widget.dbug {
  .widget-content {
    background: red; 
    .chart { background: lime; }
    .data { background: cyan; }
  }
}
.app-preview-widget {
  .widget-content {
    .chart, .data { display: flex; align-items: center; }
    .chart {
      width: 33%;
      justify-content: center;
      .chart-content {
        width: 100%;
        height: 0;
        padding-bottom: 100%;
        border-radius: 100%;
        position: relative;
        .donut {
          width: 100%;
          height: 100%;
          background: transparent;
          position: absolute;
          top: 0;
          left: 0;
          z-index: 10;
        }
        .amount {
          pointer-events: none;
          font-family: 'DINNextLTPro-Medium';
          // font-size: 30px;
          color: $colorBrand;
          position: absolute;
          width: 80%;
          top: 50%;
          left: 50%;
          transform: translate(-50%,-50%);
          svg {
            text {
              text-anchor: middle;
            }
          }
        }
      }
    }
    .data {
      width: 66%;
      padding-left: 15px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      .widget-title {
        color: #6B7280;
        font-size: 14px;
      }
      .amount {
        font-family: 'DINNextLTPro-Medium';
        &--main {
          color: $colorTextBase;
          font-size: 24px;
          white-space: nowrap;
          span {
            font-size: 14px;
            color: $colorTextBase;
          }
        }
        &--secondary {
          color: #6B7280;
          font-size: 20px;
          span {
            font-size: 12px;
            color: #9CA3AF;
          }
        }
        &--tertiary {
          color: #6B7280;
          font-size: 16px;
          span {
            font-size: 10px;
            color: #{$colorUnassigned}7d;
          }
        }
      }
    }
  }
  .missing {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    text-align: center;
    color: $colorUnassigned;
    font-size: 16px;
    .svg-inline--fa {
      font-size: 40px;
      display: block;
      margin-bottom: 10px;
    }
    span {
      display: block;
    }
  }
  &.costs {
    .missing{
      .svg-inline--fa { color: $colorAppCosts; }
    }
    .chart-content {
      .amount {
        color: $colorAppCosts !important;
      }
    }
  }
  &.hours {
    .missing{
      .svg-inline--fa { color: $colorAppHours; }
    }
    .chart-content {
      .amount {
        color: $colorAppHours !important;
      }
    }
  }
  &.trips {
    .missing{
      .svg-inline--fa { color: $colorAppTrips; }
    }
    .chart-content {
      .amount {
        color: $colorAppTrips !important;
      }
    }
  }
  &.invoices {
    .missing{
      .svg-inline--fa { color: $colorAppInvoices; }
    }
    .chart-content {
      .amount {
        color: $colorAppInvoices !important;
      }
    }
  }
}
</style>
